import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../misc/Button";
import { useImpersonation } from "../../hooks/useImpersonation";
import { cn } from "../../utils/classMerger";

interface NicheSetupPromptProps {
    onRecheck: () => Promise<void>;
    // The last re-read failed (useNiche's error), so "not saved yet" would be the wrong answer.
    recheckFailed?: boolean;
    // Inside another step (Add Post's Basic Info): a bordered box with a smaller heading.
    compact?: boolean;
}

// My Niche Video needs a saved niche (spec §4.3). My Niche opens in a new tab so
// the post stays open; "I've set it up" re-reads the overview. A saved niche unmounts
// this prompt, so a check that ends with it still showing found none.
const NicheSetupPrompt: React.FC<NicheSetupPromptProps> = ({ onRecheck, recheckFailed = false, compact = false }) => {
    const Heading = compact ? "h3" : "h2";
    // Impersonation lives in this tab only: a new tab is the admin's own account, and a
    // full page load ends the session. So My Niche opens here, routed client-side.
    const isImpersonated = useImpersonation();
    const [checking, setChecking] = useState(false);
    const [checked, setChecked] = useState(false);
    const mountedRef = useRef(true);
    // Set on every mount: StrictMode's dev remount runs the cleanup once before the real mount.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const recheck = async (): Promise<void> => {
        setChecking(true);
        try {
            await onRecheck();
        } finally {
            if (mountedRef.current) {
                setChecking(false);
                setChecked(true);
            }
        }
    };

    let result = "";
    if (checked && !checking) {
        result = recheckFailed
            ? "Couldn't check just now. Try again."
            : `We don't see a saved niche yet. Finish My Niche${isImpersonated ? "" : " in the other tab"} and press Save my niche, then try again.`;
    }

    return (
        <div
            data-testid="niche-setup-prompt"
            className={cn("space-y-3 text-center", compact && "rounded-md border border-gray-200 p-4")}
        >
            <Heading className={cn("font-bold text-primary", compact ? "text-base" : "text-xl")}>Set up My Niche first</Heading>
            <p className="text-sm text-gray-600">
                {isImpersonated
                    ? "Your topics and scripts come from your niche. It takes about 10 minutes. My Niche opens in this tab, so you will need to start this post again."
                    : "Your topics and scripts come from your niche. It takes about 10 minutes, and this post stays open while you do it."}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
                <Button asChild>
                    {isImpersonated ? (
                        <Link to="/my-niche">Set up My Niche</Link>
                    ) : (
                        <a href="/my-niche" target="_blank" rel="noopener noreferrer">
                            Set up My Niche
                        </a>
                    )}
                </Button>
                <Button type="button" variant="outline" disabled={checking} onClick={() => void recheck()}>
                    {checking ? "Checking…" : "I've set it up"}
                </Button>
            </div>
            {/* Always mounted, so screen readers announce the result when it fills in. */}
            <p role="status" className={cn("text-sm text-[#d23600]", !result && "sr-only")}>
                {result}
            </p>
        </div>
    );
};

export default NicheSetupPrompt;
